import _ from 'lodash';
import React from 'react';
import { useBachAcHistoryContext } from '/@contexts/bach-ac-history-context';
import { useAppContext } from '/@contexts/global-app-context';
import { AcSession, BachSessionId } from '/@services/generated/graphql-types';
import {
    getSession,
    useStatefulMutation,
    useStatefulQuery,
} from '/@services/stateful/stateful-service-hooks';
import {
    DocumentNode,
    gql,
    MutationFunctionOptions,
    MutationHookOptions,
    MutationTuple,
    QueryHookOptions,
    QueryResult,
    useApolloClient,
    useMutation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { RATE_LIMIT_TYPE } from '../../utils/rate-limit.util';
import { useCancelableMutation } from '../cancelable/cancelable-hooks';
import { useRateLimitedMutation } from '../rate-limit/rate-limit-hooks';

type MutationMutator<TData, TVariables> = MutationTuple<
    TData,
    Omit<TVariables, 'session'>
>[0];
type MutationState<TData, TVariables> = MutationTuple<
    TData,
    Omit<TVariables, 'session'>
>[1];

// Mock session ID used in unit and integration tests
export { TEST_SESSION_ID } from '../stateful/stateful-service-hooks';

// Rate limit period to use for multiple successive sage requests
const SAGE_REQUEST_RATE_LIMIT_MS = 500;

export function useAcSession(answerSessionId: string): AcSession {
    const client = useApolloClient();

    const bachSession = getSession(
        client,
        answerSessionId,
        'BachSessionId',
        gql`
            {
                sessionId
                acSession {
                    sessionId
                    genNo
                }
            }
        `,
    );

    return bachSession.acSession;
}

export function useGetBachSessionWithAcSession(
    answerSessionId: string,
): () => BachSessionId {
    const client = useApolloClient();

    const getBachSessionWithAcSession = React.useCallback(() => {
        return getSession(
            client,
            answerSessionId,
            'BachSessionId',
            gql`
                {
                    sessionId
                    genNo
                    acSession {
                        sessionId
                        genNo
                    }
                }
            `,
        );
    }, [answerSessionId]);

    return getBachSessionWithAcSession;
}

function useGenNoWorkingSetVariables() {
    const { sessionService } = useAppContext();
    const bachAcHistoryContext = useBachAcHistoryContext();

    return () =>
        sessionService.isAnswerUndoStackEnabled()
            ? {
                  session: {
                      genNoWorkingSet: bachAcHistoryContext?.getAcGenNoWorkingSet(),
                  },
              }
            : {};
}

export function useSearchMutationBase<TData, TVariables>(
    answerSessionId: string,
    mutation: DocumentNode,
    options: MutationHookOptions<TData, TVariables> = {},
): [
    MutationMutator<TData, TVariables>,
    MutationMutator<TData, TVariables>,
    () => void,
    MutationState<TData, TVariables>,
] {
    const acSession = useAcSession(answerSessionId);
    const getGenNoWorkingSetVariables = useGenNoWorkingSetVariables();

    const [
        cancelableMutationMutate,
        cancelMutation,
        cancelableMutationState,
    ] = useCancelableMutation(
        useMutation<TData, TVariables>(mutation, options),
    );

    const [
        rateLimitedMutationMutateImmediate,
        rateLimitedMutationMutateRateLimited,
        ,
        rateLimitedMutationState,
    ] = useRateLimitedMutation(
        [cancelableMutationMutate, cancelableMutationState],
        RATE_LIMIT_TYPE.THROTTLE,
        SAGE_REQUEST_RATE_LIMIT_MS,
    );

    // Perf note: we don't need a useCallback on this function because the inner function already
    // uses memoization internally. Hence if this function is called with the same input ref, the
    // output ref should also be unchanged (assuming no other external changes).
    const wrapWithStatefulMutation = (
        mutate: MutationMutator<TData, TVariables>,
    ) => {
        return useStatefulMutation<TData, TVariables>(
            [mutate, rateLimitedMutationState],
            acSession.sessionId,
            'AcSession',
            gql`
                {
                    sessionId
                    genNo
                }
            `,
        );
    };

    const [
        statefulMutationMutateImmediate,
        statefulMutationState,
    ] = wrapWithStatefulMutation(rateLimitedMutationMutateImmediate);
    const [statefulMutationMutateRateLimited] = wrapWithStatefulMutation(
        rateLimitedMutationMutateRateLimited,
    );

    const wrapMutateWithHistory = (
        mutate: MutationMutator<TData, TVariables>,
    ) => {
        return (
            opts: MutationFunctionOptions<
                TData,
                Omit<TVariables, 'session'>
            > = {},
        ) => {
            const variables = _.merge(
                {},
                options.variables,
                opts.variables,
                getGenNoWorkingSetVariables(),
            );

            return mutate({
                ...opts,
                variables,
            });
        };
    };

    const mutateImmediateWithHistory = React.useCallback(
        wrapMutateWithHistory(statefulMutationMutateImmediate),
        [statefulMutationMutateImmediate],
    );
    const mutateRateLimitedWithHistory = React.useCallback(
        wrapMutateWithHistory(statefulMutationMutateRateLimited),
        [statefulMutationMutateRateLimited],
    );

    return [
        mutateImmediateWithHistory,
        mutateRateLimitedWithHistory,
        cancelMutation,
        statefulMutationState,
    ];
}

export function useSearchQueryBase<TData, TVariables>(
    answerSessionId: string,
    query: DocumentNode,
    options: QueryHookOptions<TData, TVariables> = {},
): QueryResult<TData, TVariables> {
    const acSession = useAcSession(answerSessionId);
    const getGenNoWorkingSetVariables = useGenNoWorkingSetVariables();

    const finalOptions = _.merge({}, options, {
        variables: getGenNoWorkingSetVariables(),
    });

    return useStatefulQuery<TData, TVariables>(
        acSession.sessionId,
        'AcSession',
        gql`
            {
                sessionId
                genNo
            }
        `,
        query,
        finalOptions,
    );
}
